/**
 * ポップアップを開く操作
 *
 * DOM (popups) と「エディタへの反映」を結ぶ。操作はどれも「実行 → 閉じる →
 * 状態同期」なのでその定型もここで畳む
 *
 * エディタ操作は @tiptap の型や chain を模さず、意味のある単位 (RichTextEditorActions) で受け取る。
 * ここが持つのは「どの操作をいつ使うか」の判断だけ
 */
import { ALIGN_ITEMS, COLOR_PALETTE, LINEHEIGHT_LIST } from './config';
import { createAlignPopup, createColorPopup, createLineHeightPopup, createLinkPopup, createTablePopup } from './popups';
import type { RichTextAlignItem } from './config';

/** テーブル挿入グリッドの最大行数・列数 */
const TABLE_GRID_MAX = 8;

/** リンクに付ける属性。常に同じウィンドウで開く (「新しいウィンドウで開く」は削除済み) */
export interface LinkAttrs {
  href: string;
  target: '_self';
}

/** リンク編集を開いた時点のエディタ側の状況 */
export interface LinkContext {
  /** 既存リンクの href。新規なら空文字 */
  href: string;
  /** 既存リンクの表示テキスト (または選択中のテキスト) */
  text: string;
  /** 既存リンク全体の範囲。リンク上にいなければ null。replaceLink へ返すだけなので型は問わない */
  range: unknown | null;
  /** カーソルのみ (選択範囲なし) か */
  isSelectionEmpty: boolean;
}

/**
 * ポップアップから呼ぶエディタ操作。呼び出し側が Tiptap のコマンドで実装する
 * 「何をするか」を名前で表し、chain の組み立て方はこちらに漏らさない
 */
export interface RichTextEditorActions {
  /** 現在のブロック属性 (段落と見出しの両方を見る)。未設定なら null */
  blockAttr(name: string): string | null;
  setBackgroundColor(color: string): void;
  unsetBackgroundColor(): void;
  setColor(color: string): void;
  unsetColor(): void;
  setTextAlign(align: string): void;
  changeIndent(delta: number): void;
  setLineHeight(lineHeight: string): void;
  insertTable(rows: number, cols: number): void;
  readLinkContext(): LinkContext;
  /** 既存リンクの属性だけ更新する (テキストノードを作り直さず他のマークを保つ) */
  updateLinkAttrs(attrs: LinkAttrs): void;
  /** 既存リンクを表示テキストごと置き換える (二重挿入を防ぐ) */
  replaceLink(range: unknown, attrs: LinkAttrs, text: string): void;
  /** カーソル位置へ新しいリンクを挿入する */
  insertLink(attrs: LinkAttrs, text: string): void;
  /** 選択範囲にリンクを付ける */
  setLinkOnSelection(attrs: LinkAttrs): void;
  unsetLink(): void;
}

export const createPopupOpeners = ({
  actions,
  popups,
  afterAction,
}: {
  actions: RichTextEditorActions;
  popups: { open(popup: HTMLElement, anchor: HTMLElement, className: string): void; close(): void };
  /** 操作後の状態同期 (ツールバー表示の更新) */
  afterAction(): void;
}) => {
  /** ポップアップ内の操作は「実行 → 閉じる → 状態同期」で共通 */
  const runAndClose = (action: () => void) => (): void => {
    action();
    popups.close();
    afterAction();
  };

  return {
    openColor: (anchor: HTMLElement): void => {
      const popup = createColorPopup({
        palette: COLOR_PALETTE,
        columns: [
          {
            title: '背景色',
            onPick: (color) => runAndClose(() => actions.setBackgroundColor(color))(),
            onUnset: runAndClose(() => actions.unsetBackgroundColor()),
          },
          { title: '文字色', onPick: (color) => runAndClose(() => actions.setColor(color))() },
        ],
      });
      popups.open(popup, anchor, 'codmon-rte__color-popup');
    },

    openAlign: (anchor: HTMLElement): void => {
      const popup = createAlignPopup({
        items: ALIGN_ITEMS,
        currentAlign: actions.blockAttr('textAlign') || 'left',
        onSelect: (cmd: RichTextAlignItem['cmd']) =>
          runAndClose(() => {
            if (cmd === 'indent') actions.changeIndent(1);
            else if (cmd === 'outdent') actions.changeIndent(-1);
            else actions.setTextAlign(cmd);
          })(),
      });
      popups.open(popup, anchor, 'codmon-rte__align-popup');
    },

    openLineHeight: (anchor: HTMLElement): void => {
      const popup = createLineHeightPopup({
        values: LINEHEIGHT_LIST,
        current: actions.blockAttr('lineHeight'),
        onSelect: (value) => runAndClose(() => actions.setLineHeight(value))(),
      });
      popups.open(popup, anchor, 'codmon-rte__lineheight-popup');
    },

    openTable: (anchor: HTMLElement): void => {
      const popup = createTablePopup({
        max: TABLE_GRID_MAX,
        onSelect: (rows, cols) => runAndClose(() => actions.insertTable(rows, cols))(),
      });
      popups.open(popup, anchor, 'codmon-rte__table-popup');
    },

    openLink: (anchor: HTMLElement): void => {
      const context = actions.readLinkContext();

      const applyLink = ({ href, text }: { href: string; text: string }): void => {
        const attrs: LinkAttrs = { href, target: '_self' };
        if (context.range && text === context.text) {
          // 表示テキストが同じなら属性だけ更新する。置き換えると太字や文字色が失われるため
          actions.updateLinkAttrs(attrs);
        } else if (context.range) {
          // 表示テキストが変わったらリンク全体を置き換える (二重挿入を防ぐ)
          actions.replaceLink(context.range, attrs, text);
        } else if (context.isSelectionEmpty) {
          actions.insertLink(attrs, text);
        } else {
          actions.setLinkOnSelection(attrs);
        }
      };

      const popup = createLinkPopup({
        initialText: context.text,
        initialHref: context.href || 'https://',
        onApply: (link) => runAndClose(() => applyLink(link))(),
        onRemove: runAndClose(() => actions.unsetLink()),
      });
      popups.open(popup.el, anchor, 'codmon-rte__link-popup');
      popup.focusInitialInput();
    },
  };
};
